import { COMMA, ENTER, SPACE, TAB } from '@angular/cdk/keycodes';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatChipInput, MatChipInputEvent } from '@angular/material/chips';
import { MatSelectChange } from '@angular/material/select';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ICohort, ILine, IPaymentDetails, IProfile, IPurchasedAllotment, IManagementGroup, IResponse } from '@interfaces/index';
import { CookieService } from 'ngx-cookie-service';
import { IInventory } from 'projects/ui-components/src/interfaces/IOrganization';
import { ISearch, IValidate } from 'projects/ui-components/src/interfaces/IUser';
import { Observable, catchError, filter, finalize, map, of, startWith, switchMap, take, tap } from 'rxjs';
import { AllotmentService } from '../services/allotment.service';
import { AppService } from '@ui-components/services';
import { AssessmentService } from '../services/assessment.service';
import { CohortService } from '../services/cohort.service';
import { OrganizationService } from '../services/organization.service';
import { PaymentService } from '../services/payment.service';
import { UserService } from '../services/user.service';
import { ProfileService } from '../services/profile.service';

@Component({
  selector: 'app-invite',
  templateUrl: './invite.component.html',
  styleUrl: './invite.component.scss'
})
export class InviteComponent {

  TAB_INDEXES = {
    LINK: 0,
    EMAIL_MENTION: 1,
    COHORT: 2,
  };

  selectedManagementGroup: IManagementGroup | null = null;
  assessments: IPurchasedAllotment[] = [];
  assessment_id: number | null = null;
  assessment_name: IPurchasedAllotment | undefined;
  assessments_purchased: ILine[] = [];
  chipControl = new FormControl();
  cohorts: ICohort[] = [];
  emails: IValidate | null = null;
  expiration_date: string | null = null;
  filteredOptions: Observable<ISearch[]> | null = null;
  form: FormGroup;
  generated_link: string | null = null;
  hideTab: boolean = false;
  loading = false;
  loadingInventory = false;
  orgs: IInventory[] = [];
  org_id: number | null = null;
  org_inventory: IInventory | undefined;
  parent_org_inventory: IInventory | undefined = undefined;
  profile: IProfile | null = null;
  person_id: number | null = null;
  person_email: string | null = null;
  registered_emails: string[] = [];
  selected_chips: string[] = [];
  selected_email_chips: string[] = [];
  selected_ids: number[] = [];
  selected_index = 0;
  selected_tab_index = 0;
  selected_unregistered_email_chips: string[] = [];
  session_id: string | null = null;
  separator_keys_codes: number[] = [ENTER, COMMA, SPACE, TAB];
  tabCount = 3;
  unregistered_users: string[] = [];

  @ViewChild('chipInput', { static: false }) chipInput!: ElementRef;

  constructor(
    protected _allotment_service: AllotmentService,
    protected _app_service: AppService,
    protected _profile_service: ProfileService,
    protected _assessment_service: AssessmentService,
    protected _cohort_service: CohortService,
    protected _cookie_service: CookieService,
    protected _fb: FormBuilder,
    protected _org_service: OrganizationService,
    protected _payment_service: PaymentService,
    protected _route: ActivatedRoute,
    protected _router: Router,
    protected _user_service: UserService
  ) {
    this.form = this._fb.group({
      selected_assessment_id: new FormControl('', Validators.required),
      selected_org_id: new FormControl(''),
      cohort_id: new FormControl(''),
      close_dt: new FormControl(''),
    })
  }

  ngOnInit() {
    this._app_service.selectedManagementGroup$.subscribe((group) => {
      this.selectedManagementGroup = group;
      if (this.selectedManagementGroup != null) {
        this.getOrgsAndInventory();
      }
    });
    this.loading = true;
    this._route.queryParams
      .pipe(switchMap((params: Params) => of(params)))
      .subscribe((params) => {
        this.session_id = params['session_id'];
        this.assessment_id = +params['assessment_id'];
        this.org_id = +params['org_id'];
        this.person_id = +params['person_id'];

        if (this.assessment_id) {
          this.form.patchValue({
            selected_assessment_id: {
              assessment_id: this.assessment_id,
            },
          });
        }

        if (this.person_id) {
          this.fetchPersonEmail(this.person_id);
          this.selected_tab_index = this.TAB_INDEXES.EMAIL_MENTION;
        }

        const selectOrgId = this._cookie_service.get("selected_management_group_org_id");
        if (this.session_id) {
          this.form.get('selected_org_id')?.setValidators(Validators.required);
          this.form.get('selected_org_id')?.updateValueAndValidity();
          this.getPaymentDetails(this.session_id);
        } else if (!selectOrgId){
          this.getOrgsAndInventory();
        }

        this.getPurchasedAllotments();
        this.filterOptions();

        if (!this.form.invalid) {
          this.createLink();
        }
      });
  }

  fetchPersonEmail(person_id: number): void {
    if (!person_id) {
      this.person_email = null;
      return;
    }
    this._profile_service.getBasic(person_id).subscribe({
      next: (response) => {
        this.person_email = response.data?.email_address;
        if(this.person_email && !this.selected_email_chips.includes(this.person_email)) {
          this.selected_email_chips.push(this.person_email);
        }
      },
      error: (error ) => {
        this.person_email = null;
        console.error('Error fetching person email:', error?.status);
      }
    });
  }

  addChip(event: MatChipInputEvent): void {
    const { chipInput, value } = event;

    if (value && value.trim()) {
      const trimmedValue = value.trim();

      if (this.isValidEmail(trimmedValue)) {
        this.loading = true;
        this.handleEmailValidation(trimmedValue).subscribe({
          next: () => {
            this.loading = false;
          },
          error: () => {
            this.loading = false;
            this._app_service.notify("Error validating email", "error");
          }
        });
      } else {
        this._app_service.notify("Invalid email format", "error");
        this.clearInput(chipInput);
      }
    }
  }

  checkTab(): number {
    return this.selected_tab_index;
  }

  clearFilteredOptions() {
    this.chipControl.setValue('');
  }

  clearInput(chipInput: MatChipInput | null): void {
    if (chipInput) {
      chipInput.clear()
    }
    this.clearFilteredOptions();
  }

  copyToClipboard(link: string) {
    navigator.clipboard.writeText(link).then(() => {
      this._app_service.notify('Link copied to clipboard!', 'success');
    }).catch(() => {
      this._app_service.notify('Failed to copy link. Try again.', 'error');
    });
  }

  filterOptions(): void {
    this.filteredOptions = this.chipControl.valueChanges.pipe(
      startWith(''),
      filter((query): query is string => typeof query === 'string'),
      switchMap((input: string) => {
        if (this.isValidInput(input) && input.length > 3) {
          return input.startsWith('@') ? this.getSearchResults(input.substring(1).trim()) : of([])
        }
        else {
          return of([])
        }
      }
      )
    );
  }

  get formattedTooltip(): string {
    const available_inventory = this.selectedOrg?.available_inventory !== undefined ? this.selectedOrg?.available_inventory : this.org_inventory?.available_inventory
    if (this.orgs?.length === 1) {
      return ` Total of ${available_inventory} assessments available`
    }
    return `${available_inventory} From Org Selected \n + \n
    ${this.parent_org_inventory?.available_inventory!} From Parent Org \n = \n
    ${available_inventory! + this.parent_org_inventory?.available_inventory!} Available`;
  }

  createLink() {
    if (this.form.invalid) {
      this._app_service.notify('Please fill in all required fields.', 'error');
      return;
    }

    this.loading = true;

    const payload = {
      assessment_id: this.form.get('selected_assessment_id')?.value?.assessment_id || this.assessment_id,
      org_id: this.form.get('selected_org_id')?.value?.org_id || this.org_id,
      close_dt: this.form.get('close_dt')?.value || null,
    };

    this._assessment_service.createLink(payload)
      .pipe(take(1))
      .subscribe({
        next: (response: IResponse<{ link: string; expiration_date?: string; }>) => {
          this.loading = false;
          if (Number(response.code) === 200 && response.data) {
            this.generated_link = response.data.link || null;
            this.expiration_date = response.data.expiration_date ?? null;
          } else {
            this._app_service.notify(response.message || 'Failed to generate link.', 'error');
          }
        },
        error: (error: unknown) => {
          this.loading = false;
          console.error('Error generating link:', (error as { status?: number })?.status);
          this._app_service.notify('Failed to generate link. Please try again later.', 'error');
        },
      });
  }

  getCohorts() {
    this._app_service.getProfile().pipe(
      switchMap((response: IResponse<IProfile>) => {
        this.profile = response.data;
        return this._cohort_service.getAll(this.profile.org_id)
      })
    ).subscribe((response: IResponse<ICohort[]>) => {
      this.cohorts = response.data;
    })
  };

  getOrgsAndInventory() {
    this.loadingInventory = true;
    this._org_service.inventory(this.form.get('selected_assessment_id')?.value.assessment_id || this.assessment_id)
      .pipe(
        finalize(() => {
          this.loadingInventory = false;
        })
      )
      .subscribe((response: IResponse<IInventory[]>) => {
        this.orgs = response.data;
        this.parent_org_inventory = response.data?.find(a => a.level === null)
        this.org_inventory = this.orgs?.find(a => a.level !== null && a.org_id === this.org_id)!;
      });
  }

  getPaymentDetails(session_id: string) {
    this._payment_service.getPaymentDetails(session_id as string).pipe(
      switchMap(
        (response: IResponse<IPaymentDetails>) => {
          this.assessments_purchased = response.data.line_items;
          return this._payment_service.updatePayment(session_id as string);
        }
      ),
      catchError((err) => {
        return of({ code: 400, data: err });
      })
    ).subscribe(
      (response: IResponse<void>) => {
        this.loading = false;
      },
    )
  }

  getPurchasedAllotments() {
    this._allotment_service.getPurchasedAllotments().subscribe(
      (response: IResponse<IPurchasedAllotment[]>) => {
        this.assessments = response.data;
        this.assessment_name = this.assessments.find(a => a.assessment_id === this.assessment_id);
      }
    );
  }

  getSearchResults(search: string): Observable<ISearch[]> {
    const person_id = parseInt(this._cookie_service.get('person_id'));

    return this._user_service.searchForInvite(person_id, search).pipe(
      map((response: IResponse<{ content: ISearch[] }>) => response.data.content),
      catchError(err => {
        console.error('Error fetching search results:', err?.status);
        return of([]);
      })
    );
  }

  goHome(): void {
    this._router.navigate(['/']);
  }

  handleEmailValidation(email: string): Observable<IValidate> {
    const person_id = parseInt(this._cookie_service.get('org_id'));

    return this.validateEmail(person_id, email).pipe(
      tap(() => {
        if (this.chipInput) {
          this.chipInput.nativeElement.value = '';
        }
        this.clearFilteredOptions();
      }),
      finalize(() => {
        this.loading = false;
      })
    );
  }

  invite() {
    const inputElement = this.chipInput?.nativeElement;
    const inputValue = inputElement?.value?.trim();

    if (inputValue) {
      if (this.isValidEmail(inputValue)) {
        this.loading = true;
        this.handleEmailValidation(inputValue).subscribe({
          next: () => {
            this.loading = false;
            this.submitInvite();
          },
          error: () => {
            this.loading = false;
            this._app_service.notify("Error validating email", "error");
          }
        });
      } else {
        this._app_service.notify("Invalid email format", "error");
      }
    } else {
      this.submitInvite();
    }
  }

  nextTab() {
    if (this.selected_tab_index < this.tabCount - 1) {
      this.selected_tab_index++;
    }
  }

  submitInvite() {
    this.loading = true;
    const selected_org_id = this.form.get('selected_org_id')?.value.org_id;
    const provided_org_id = this.org_id ? this.org_id : this.orgs[0].org_id;

    this._assessment_service.invite({
      selected_assessment_id: this.form.get('selected_assessment_id')?.value.assessment_id || this.assessment_id,
      selected_org_id: selected_org_id ? selected_org_id : provided_org_id,
      ...(this.form.get('close_dt')?.value ? { close_dt: this.form.get('close_dt')?.value.format('yyyy-MM-DD') } : {}),
      ...(this.checkTab() === this.TAB_INDEXES.COHORT && this.form.get('cohort_id')?.value ? { selected_cohort_id: this.form.get('cohort_id')?.value } : {}),
      ...(this.checkTab() === this.TAB_INDEXES.EMAIL_MENTION && this.selected_ids.length > 0 && { registered_users: this.selected_ids }),
      ...(this.checkTab() === this.TAB_INDEXES.EMAIL_MENTION && this.registered_emails.length > 0 && { registered_emails: this.registered_emails }),
      ...(this.checkTab() === this.TAB_INDEXES.EMAIL_MENTION && this.unregistered_users.length > 0 && { unregistered_users: this.unregistered_users })
    }).subscribe(
      (response: IResponse<{}>) => {
        if (response.code === 200) {
          this._app_service.notify(response.message!, 'success');
          this._router.navigate(['/']);
        } else {
          this._app_service.notify(response.message!, 'Error')
        }
        this.loading = false;
      }
    )
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private isValidInput(query: string): boolean {
    return query.startsWith('@') || this.isValidEmail(query);
  }

  onAssessmentSelected(event: MatSelectChange) {
    this.form.patchValue({
      selected_assessment_id: event.value
    });
  }

  onOrgSelected(event: MatSelectChange) {
    this.form.patchValue({
      selected_org_id: event.value
    });

    this.createLink();
  }


  onTabClicked(event: MatTabChangeEvent): void {
    const selected_index = event.index;

    if (selected_index === this.TAB_INDEXES.COHORT) {
      this.getCohorts();
    }
  }

  prevTab() {
    if (this.selected_tab_index > 0) {
      this.selected_tab_index--;
    }
  }

  removeChip(chip: string): void {
    const chipSources = [
      { chips: this.selected_chips, payload: this.selected_ids },
      { chips: this.selected_email_chips, payload: this.registered_emails },
      { chips: this.selected_unregistered_email_chips, payload: this.unregistered_users },
    ];

    for (const source of chipSources) {
      const index = source.chips.indexOf(chip);
      if (index >= 0) {
        source.chips.splice(index, 1);
        source.payload.splice?.(index, 1) || (source.payload = [...source.chips]);
        break;
      }
    }

    this.clearFilteredOptions();
  }

  get selectedOrg(): { org_name: string; available_inventory: number } | null {
    const value = this.form.get('selected_org_id')?.value;
    return value ? { org_name: value.org_name, available_inventory: value.available_inventory } : null;
  }

  selectOption(event: any): void {
    const selectedUser = event.option.value;
    const fullName = `${selectedUser.first_name} ${selectedUser.last_name}`;
    const id = selectedUser.person_id;
    this.selected_chips.push(fullName);
    this.selected_ids.push(id);

    this.chipInput.nativeElement.value = '';
  }

  validateEmail(person_id: number, email: string): Observable<IValidate> {
    return this._user_service.validateInviteEmail(person_id, email).pipe(
      map((response: IResponse<IValidate>) => {
        this.emails = response.data;
        return this.emails;
      }),
      tap(() => {
        if (this.emails && this.emails.count > 0) {
          this.registered_emails.push(this.emails.email);
          this.selected_email_chips.push(this.emails.email);
        } else {
          this.unregistered_users.push(this.emails?.email!);
          this.selected_unregistered_email_chips.push(this.emails?.email!)
        }
      }),
      catchError(err => {
        console.error('Error validating email:', err?.status);
        return of();
      })
    );
  }

}